import React from "react"
import { Redirect } from "react-router-dom"

// Authentication
import Login from "pages/Authentication/Login"
import Logout from "pages/Authentication/Logout"
import CambiarClave from "pages/Authentication/CambiarClave"

// BDSJEA-14: dos formas de proteger una ruta.
//
// rolesRequeridos: rol fijo. SOLO para los cuatro objetos no delegables, que
// el backend también tiene clavados en ADMINISTRADOR.
//
// objetoRequerido: se resuelve contra la matriz de Permisos por Perfil, igual
// que el backend. Es lo que corresponde para el resto.
//
// Una ruta sin ninguno de los dos queda librada al componente Permission, que
// falla ABIERTO: si el objeto no está entre los permisos del usuario, no
// encuentra con qué comparar y deja pasar. Por eso toda pantalla que mapee a
// un objeto tiene que declarar acá cuál es.
const ADMIN = ["ADMINISTRADOR"]

// Admin
import UserList from "pages/Admin/UserList"
import ProfileList from "pages/Admin/ProfileList"
import AuditoriaLogs from "pages/Admin/AuditoriaLogs/Index"
import TablasGenerales from "pages/Administracion/TablasGenerales/Index"
import DivisionGeografica from "pages/Administracion/DivisionGeografica/Index"
import Page404 from "pages/Page404"

// AGRO
import BusquedaProductor from "pages/Agro/BusquedaProductor"
import BusquedaEstablecimiento from "pages/Agro/BusquedaEstablecimiento"
import ProductorDetalle from "pages/Agro/Productor"
import AltaProductor from "pages/Agro/AltaProductor"
import DashboardAgro from "pages/Agro/Dashboard"
import AdministracionAgropecuario from "pages/Agro/Administracion"
import AdministracionCredenciales from "pages/Agro/Credenciales"

import FormularioCarga from "pages/Agro/Evaluacion/FormularioCarga"
import ResumenEjecutivo from "pages/Agro/Evaluacion/ResumenEjecutivo"
import ListaEvaluaciones from "pages/Agro/Evaluacion/ListaEvaluaciones"

// OnBoarding
import OnBoarding from "pages/OnBoarding/Index"
import Success from "pages/OnBoarding/Success"
import Monitoreo from "pages/Monitoreo"

// Service Health
import ServiceHealthDashboard from "pages/ServiceHealthDashboard/Index"

const authProtectedRoutes = [
  // Admin
  { path: "/usuarios", component: UserList, rolesRequeridos: ADMIN },
  // Sin permissionBy: cualquier usuario autenticado tiene que poder cambiar
  // su propia clave, incluso cuando entro con una provisoria. BDSJEA-129
  { path: "/cambiar-clave", publico: true, component: CambiarClave },
  {
    path: "/permisos-por-perfil",
    component: ProfileList,
    rolesRequeridos: ADMIN,
  },
  { path: "/auditoria-logs", component: AuditoriaLogs, rolesRequeridos: ADMIN },
  {
    path: "/tablasgenerales",
    objetoRequerido: "/tablasgenerales",
    component: TablasGenerales,
    permissionBy: "/Catálogo de Tablas",
  },
  {
    path: "/divisiongeografica",
    objetoRequerido: "/divisiongeografica",
    component: DivisionGeografica,
    permissionBy: "/Catálogo de Tablas",
  },
  { path: "/404", publico: true, component: Page404, error: true },

  // AGRO
  {
    path: "/agro/dashboard",
    objetoRequerido: "/agro/dashboard",
    component: DashboardAgro,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/busqueda/productor",
    objetoRequerido: "/agro/busqueda/productor",
    component: BusquedaProductor,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/busqueda/establecimiento",
    objetoRequerido: "/agro/busqueda/productor",
    component: BusquedaEstablecimiento,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/productor/:id",
    objetoRequerido: "/agro/busqueda/productor",
    component: ProductorDetalle,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/alta-productor",
    objetoRequerido: "/agro/alta-productor",
    accionRequerida: "modificacion",
    component: AltaProductor,
    permissionBy: "/alta->generar evaluación",
  },
  // BDSJEA-136: /agro/precios se elimina como ruta propia. Precios de Mercado
  // pasa a ser una solapa de /agro/admin y el objeto de menu 44 queda inactivo
  // (migracion V0221), con lo cual esta ruta quedaria denegada por el router.
  {
    path: "/agro/admin",
    objetoRequerido: "/agro/admin",
    component: AdministracionAgropecuario,
    permissionBy: "/alta->generar evaluación",
  },

  // Ruta propia y no un tab de /agro/admin: administrar precios de granos no
  // es lo mismo que rotar la API key de produccion, y con ruta separada se
  // puede restringir por separado cuando este BDSJEA-13.
  {
    path: "/agro/credenciales",
    rolesRequeridos: ADMIN,
    component: AdministracionCredenciales,
    permissionBy: "/alta->generar evaluación",
  },

  // Evaluación crediticia
  {
    path: "/agro/evaluaciones",
    objetoRequerido: "/agro/busqueda/productor",
    exact: true,
    component: ListaEvaluaciones,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/evaluaciones/nueva",
    accionRequerida: "modificacion",
    objetoRequerido: "/agro/busqueda/productor",
    exact: true,
    component: FormularioCarga,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/evaluaciones/:idEvaluacion",
    objetoRequerido: "/agro/busqueda/productor",
    exact: true,
    component: ResumenEjecutivo,
    permissionBy: "/alta->generar evaluación",
  },
  {
    path: "/agro/productores/:idProductor/evaluacion/:idEvaluacion",
    objetoRequerido: "/agro/busqueda/productor",
    exact: true,
    component: ResumenEjecutivo,
    permissionBy: "/alta->generar evaluación",
  },

  // OnBoarding
  {
    path: "/onboarding",
    objetoRequerido: "/onboarding",
    component: OnBoarding,
  },
  { path: "/success", publico: true, component: Success },
  { path: "/monitoreo", objetoRequerido: "/monitoreo", component: Monitoreo },

  {
    path: "/",
    publico: true,
    exact: true,
    component: () => <Redirect to="/agro/dashboard" />,
  },

  {
    path: "*",
    publico: true,
    exact: true,
    component: () => <Redirect to="/404" />,
  },
]

const publicRoutes = [
  { path: "/cerrar-sesion", component: Logout },
  { path: "/iniciar-sesion", component: Login },
  {
    path: "/estado-servicios",
    publico: true,
    component: ServiceHealthDashboard,
  },
]

export { publicRoutes, authProtectedRoutes }
